import { osIsWindows, type SelectedOs } from "@/modules/compute/os/os.ts";
import type { VmSizePackage } from "@/modules/compute/VmSize.types.ts";
import {
    clampToRangeValues,
    filterActiveOsPackages,
    getDefaultPackage,
    getSizeRanges,
    isSizeOutOfRange,
    packageToSizeValue,
    type SizeRanges,
    type SizeValue,
    type VmSizeParams,
} from "@/modules/compute/vmSizeSelectUtils.ts";
import { showError } from "@/modules/error/errorStream.ts";
import type { IFormModel } from "@/utils/getField.tsx";
import type { GuestLimits } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { isDeepEqual } from "remeda";

export interface SizeInputs {
    size: SizeValue;
    size_ranges: SizeRanges;
}

/** Common data necessary to calculate sizes */
export interface SizeDeps {
    readonly _packages: VmSizePackage[];
    readonly _sizeParams: VmSizeParams;
    readonly _os: SelectedOs;
    readonly _diskSize: number | undefined;
}

/** Common base interface for forms that allow selecting a VM size (eg. virtual machines & services). */
export interface SizeViewModel extends SizeDeps, IFormModel<SizeInputs> {}

function getValidRanges(this: SizeDeps, size: SizeValue | undefined) {
    return getSizeRanges(this._sizeParams, this._os, {
        currentDiskSize: this._diskSize,
        cpuCount: size?.vcpu,
    });
}

function getValidPackages(this: SizeDeps): VmSizePackage[] {
    // NB: For calculating the valid ranges for packages, we disregard the current size (ie. current CPU/RAM ratio is ignored)
    const ranges = getValidRanges.call(this, undefined);
    return filterActiveOsPackages(this._packages, this._os).filter((p) => {
        return !isSizeOutOfRange(p, ranges);
    });
}

export function calculateSizeInputs(this: SizeDeps, value: SizeValue | undefined): SizeInputs {
    value ??= getDefaultSize.call(this);

    const ranges = getValidRanges.call(this, value);

    // NB: We must clamp the RAM on every change, because the size Slider might queue multiple `onSizeChange` events
    // in one event loop cycle, and all of those will have the old/unclamped `ram` value, not the clamped one.
    const ram = clampToRangeValues(ranges.ram, value.ram);
    if (ram !== value.ram) {
        console.debug("Clamped RAM to range", ram);
        value = { ...value, ram };
    }

    return {
        size: value,
        size_ranges: ranges,
    };
}

export function onSizeChange(this: SizeViewModel, value: SizeValue, updateOnlyRanges: boolean = false) {
    // console.debug("onSizeChange, value:", value);
    const { size, size_ranges } = calculateSizeInputs.call(this, value);
    if (!updateOnlyRanges) {
        this.set("size", size);
    }
    this.set("size_ranges", size_ranges);
}

export function forceSizeForOs(this: SizeViewModel) {
    const size = notNull(this.get("size"));

    // Find if the existing size is already an available package for the selected OS
    const matchingSize: SizeValue = { ...size, isCustom: false };
    const validPackages = getValidPackages.call(this);

    const matchesSize = validPackages.some((p) => isDeepEqual(packageToSizeValue(p, false), matchingSize));

    // Otherwise, get the default size for the selected OS
    onSizeChange.call(this, getDefaultSize.call(this), matchesSize);
}

export function getDefaultSize(this: SizeDeps): SizeValue {
    const validPackages = getValidPackages.call(this);
    const os = this._os;
    const isWindows = osIsWindows(os.os_name);
    const defaultPackage = getDefaultPackage(validPackages, os);

    if (!defaultPackage) {
        showError(`No default package found for OS ${JSON.stringify(os)}`);

        const OS_MINIMUMS = {
            windows: { cpu: 2, ram: 2048, disk: 40 },
            default: { cpu: 1, ram: 512, disk: 20 },
        };

        const baseCpu = this._sizeParams.cpu.min ?? OS_MINIMUMS.default.cpu;
        const baseRam = this._sizeParams.ram.min ?? OS_MINIMUMS.default.ram;
        const baseDisk = this._sizeParams.ssd.min ?? OS_MINIMUMS.default.disk;

        const floor = isWindows ? OS_MINIMUMS.windows : OS_MINIMUMS.default;

        // TODO: Should we handle the case where there is no default package for the OS?
        return {
            vcpu: Math.max(baseCpu, floor.cpu),
            ram: Math.max(baseRam, floor.ram),
            disks: Math.max(baseDisk, floor.disk),
            isCustom: true,
        };
    }

    return packageToSizeValue(defaultPackage, false);
}

export function getSizeParams(limits?: GuestLimits): VmSizeParams {
    if (!limits?.cpu || !limits?.ram_mb || !limits?.disk_gb) {
        limits = {
            cpu: { min: 1, max: 16 },
            ram_mb: { min: 512, max: 16384 },
            disk_gb: { min: 20, max: 500 },
        };
    }

    return {
        cpu: {
            parameter: "cpu",
            description: "",
            mandatory: true,
            type: "integer",
            min: limits.cpu.min,
            max: limits.cpu.max,
            limits: [
                {
                    os_name: "windows",
                    min: 2,
                },
            ],
        },
        ram: {
            parameter: "ram",
            description: "",
            mandatory: true,
            type: "integer",
            min: limits.ram_mb.min,
            max: limits.ram_mb.max,
            limits: [
                {
                    os_name: "windows",
                    min: 2048,
                },
            ],
        },
        ssd: {
            parameter: "ssd",
            description: "",
            mandatory: true,
            type: "integer",
            min: limits.disk_gb.min,
            max: limits.disk_gb.max,
            limits: [
                {
                    os_name: "windows",
                    min: 40,
                },
            ],
        },
    };
}
